// ../components/LinkToPatientModal.jsx
import React from "react";
import {
    Modal,
    Box,
    Stack,
    Typography,
    Button,
    Divider,
    TextField,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { useAuth } from "../components/TokenHandler";

const modalStyle = {
    bgcolor: "background.paper",
    p: 3,
    mx: "auto",
    mt: 6,
    width: "92%",
    maxWidth: 980,
    borderRadius: 2,
    maxHeight: "88vh",
    overflow: "auto",
};

const rowButtonSx = {
    cursor: "pointer",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
};

const disabledRowSx = {
    opacity: 0.55,
    cursor: "not-allowed",
    backgroundColor: "rgba(0,0,0,0.03)",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
};

function formatDob(dob) {
    if (!dob) return "—";
    return String(dob).slice(0, 10);
}

function parseRowTime(row) {
    const t = Date.parse(row?.updated_on || row?.created_on || 0);
    return Number.isFinite(t) ? t : 0;
}

function toDateInputValue(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function normalizeBoolean(value) {
    if (value === true) return true;
    if (value === false) return false;

    const text = String(value ?? "").trim().toLowerCase();

    if (text === "true" || text === "t" || text === "1" || text === "yes") return true;
    if (text === "false" || text === "f" || text === "0" || text === "no") return false;

    return null;
}

function getSeedPatientSearchName(externalFormRow) {
    return String(
        externalFormRow?.corrected_patient_first_name ||
        externalFormRow?.patient_first_name ||
        ""
    ).trim();
}

export default function LinkToPatientModal({
                                               open,
                                               onClose,
                                               externalFormRow = null,
                                               onLinked,
                                           }) {
    const { keycloak: kc, givenName, familyName, email } = useAuth();

    // Use corrected values if present, else original
    const displayPatientFirst =
        externalFormRow?.corrected_patient_first_name || externalFormRow?.patient_first_name || "";
    const displayPatientLast =
        externalFormRow?.corrected_patient_last_name || externalFormRow?.patient_last_name || "";
    const displayPatientName = `${displayPatientFirst} ${displayPatientLast}`.trim();

    const displayPatientDob =
        externalFormRow?.corrected_dob ||
        externalFormRow?.corrected_patient_dob ||
        externalFormRow?.dob ||
        externalFormRow?.patient_dob ||
        "";

    const displayFacilityName =
        externalFormRow?.corrected_facility_name || externalFormRow?.facility_name || "";
    const displayProviderNumber =
        externalFormRow?.corrected_provider_number || externalFormRow?.provider_number || "";

    // External form opt-in value drives doNotLink logic
    const externalFormOptIn = normalizeBoolean(externalFormRow?.optin);

    // Facility search (/search_orgs)
    const [orgSearchTerm, setOrgSearchTerm] = React.useState("");
    const [organizationOptions, setOrganizationOptions] = React.useState([]);
    const [orgLoading, setOrgLoading] = React.useState(false);
    const [orgError, setOrgError] = React.useState("");
    const [selectedFacility, setSelectedFacility] = React.useState(null);

    // Patients
    const [patientSearch, setPatientSearch] = React.useState("");
    const [patients, setPatients] = React.useState([]);
    const [patientsLoading, setPatientsLoading] = React.useState(false);
    const [patientsError, setPatientsError] = React.useState("");
    const [selectedPatient, setSelectedPatient] = React.useState(null);

    // Effective date
    const [effectiveDate, setEffectiveDate] = React.useState("");

    // do-not-link tracking
    const [doNotLinkMap, setDoNotLinkMap] = React.useState({});
    const [latestConsentByPatient, setLatestConsentByPatient] =
        React.useState({});

    // internal: fetch-once-per-facility selection (until facility changes)
    const [patientsFetchedForFacilityKey, setPatientsFetchedForFacilityKey] = React.useState("");

    // Link submit state
    const [linkSubmitting, setLinkSubmitting] = React.useState(false);
    const [linkError, setLinkError] = React.useState("");
    const [linkSuccess, setLinkSuccess] = React.useState("");

    // hide/show option lists
    const [showFacilityOptions, setShowFacilityOptions] = React.useState(true);
    const [showPatientOptions, setShowPatientOptions] = React.useState(true);

    // abort + debounce
    const orgAbortRef = React.useRef(null);
    const patientsAbortRef = React.useRef(null);
    const latestQueryRef = React.useRef("");
    const debounceRef = React.useRef(null);
    const patientDebounceRef = React.useRef(null);

    // Reset on open, default facility search to provider number (editable)
    React.useEffect(() => {
        if (!open) return;

        setOrgSearchTerm(String(displayProviderNumber ?? "").trim());
        setOrganizationOptions([]);
        setOrgLoading(false);
        setOrgError("");
        setSelectedFacility(null);

        setPatientSearch("");
        setPatients([]);
        setPatientsLoading(false);
        setPatientsError("");
        setSelectedPatient(null);
        setPatientsFetchedForFacilityKey("");
        setDoNotLinkMap({});

        setEffectiveDate(
            toDateInputValue(
                externalFormRow?.updated_on ||
                externalFormRow?.created_on ||
                new Date()
            )
        );

        setLinkSubmitting(false);
        setLinkError("");
        setLinkSuccess("");

        setShowFacilityOptions(true);
        setShowPatientOptions(true);
    }, [open, displayProviderNumber, externalFormRow]);

    // facility search function (/search_orgs)
    const fetchOrgSearchResults = React.useCallback(
        async (q) => {
            try {
                const term = String(q || "").trim();
                if (term.length < 2) {
                    setOrganizationOptions([]);
                    setOrgError("");
                    return;
                }

                if (orgAbortRef.current) orgAbortRef.current.abort();
                const controller = new AbortController();
                orgAbortRef.current = controller;

                latestQueryRef.current = term;
                setOrgLoading(true);
                setOrgError("");

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/search_orgs4?q=${encodeURIComponent(term)}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: kc?.token ? `Bearer ${kc.token}` : undefined,
                            "Content-Type": "application/json",
                        },
                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    const text = await response.text().catch(() => "");
                    throw new Error(text || `Search API error (HTTP ${response.status})`);
                }

                const data = await response.json();
                const rows = Array.isArray(data) ? data : [];

                if (term === latestQueryRef.current) {
                    setOrganizationOptions(rows);

                    // Auto-select when the provider/facility search returns exactly one match
                    if (rows.length === 1) {
                        const onlyFacility = rows[0];

                        setSelectedFacility(onlyFacility);
                        setShowFacilityOptions(false);

                        setPatients([]);
                        setPatientsError("");
                        setPatientsLoading(false);
                        setSelectedPatient(null);
                        setPatientsFetchedForFacilityKey("");
                        setDoNotLinkMap({});

                        // Seed patient search from external form first name
                        setPatientSearch(getSeedPatientSearchName(externalFormRow));
                        setShowPatientOptions(true);
                    }
                }
            } catch (err) {
                if (err?.name === "AbortError") return;
                setOrganizationOptions([]);
                setOrgError(err?.message || "Failed to search facilities.");
            } finally {
                setOrgLoading(false);
            }
        },
        [kc?.token, externalFormRow]
    );

    // Debounce facility searches when input changes
    React.useEffect(() => {
        if (!open) return;

        setShowFacilityOptions(true);

        setSelectedFacility(null);
        setSelectedPatient(null);
        setPatientSearch("");
        setPatients([]);
        setPatientsError("");
        setPatientsLoading(false);
        setPatientsFetchedForFacilityKey("");
        setDoNotLinkMap({});
        setShowPatientOptions(true);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchOrgSearchResults(orgSearchTerm);
        }, 350);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [orgSearchTerm, open, fetchOrgSearchResults]);

    // Fetch patients + do-not-link map
    const fetchPatients = React.useCallback(
        async ({ fac_id, ouid, ehr_ds } = {}) => {
            if (!fac_id || !ouid) return;

            try {
                if (patientsAbortRef.current) patientsAbortRef.current.abort();
                const controller = new AbortController();
                patientsAbortRef.current = controller;

                setPatientsLoading(true);
                setPatientsError("");
                setPatients([]);
                setSelectedPatient(null);
                setDoNotLinkMap({});

                const params = new URLSearchParams();
                params.set("fac_id", String(fac_id));
                params.set("ouid", String(ouid));

                if (ehr_ds) {
                    params.set("ehr_ds", String(ehr_ds));
                }

                const url = `${process.env.REACT_APP_API_URL}/patients2?${params.toString()}`;

                const resp = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: kc?.token ? `Bearer ${kc.token}` : undefined,
                        "Content-Type": "application/json",
                    },
                    signal: controller.signal,
                });

                if (!resp.ok) {
                    const text = await resp.text().catch(() => "");
                    throw new Error(text || `Patient lookup failed (HTTP ${resp.status})`);
                }

                const data = await resp.json();
                const patientRows = Array.isArray(data) ? data : [];
                setPatients(patientRows);

                const patientIds = [
                    ...new Set(
                        patientRows
                            .map((p) => String(p?.patient_id ?? p?.patient_unique_id ?? "").trim())
                            .filter(Boolean)
                    ),
                ];

                if (patientIds.length === 0) {
                    setDoNotLinkMap({});
                    return;
                }

                const consentResp = await fetch(
                    `${process.env.REACT_APP_API_URL}/consent/view_patient_consent`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: kc?.token ? `Bearer ${kc.token}` : undefined,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            patient_ids: patientIds,
                        }),
                        signal: controller.signal,
                    }
                );

                if (!consentResp.ok) {
                    const text = await consentResp.text().catch(() => "");
                    throw new Error(text || `Consent lookup failed (HTTP ${consentResp.status})`);
                }

                const consentJson = await consentResp.json();
                const consentRowsRaw = Array.isArray(consentJson?.rows) ? consentJson.rows : [];

// Exclude inactive consent rows from do-not-link logic.
// inactive/history-only records should not block linking.
                const activeConsentRows = consentRowsRaw.filter((row) => {
                    return normalizeBoolean(row?.active) === true;
                });

// Build latest ACTIVE consent row per patient
                const latestConsentByPatientId = {};
                setLatestConsentByPatient(latestConsentByPatientId);

                activeConsentRows.forEach((row) => {
                    const patientId = String(row?.patient_id ?? "").trim();
                    if (!patientId) return;

                    const existing = latestConsentByPatientId[patientId];
                    const rowTime = parseRowTime(row);
                    const existingTime = existing ? parseRowTime(existing) : 0;

                    if (!existing || rowTime >= existingTime) {
                        latestConsentByPatientId[patientId] = row;
                    }
                });
                // Build map of patients that CANNOT be linked.
                // Rule:
                // - If external form optin === patient's latest opt_in_flag, block linking
                // - Otherwise allow linking
                // - If no latest row exists, allow linking
                // - If either value cannot be interpreted as boolean, allow linking
                const nextDoNotLinkMap = {};

                Object.entries(latestConsentByPatientId).forEach(([patientId, row]) => {
                    const patientCurrentOptInFlag = normalizeBoolean(row?.opt_in_flag);

                    const previousMethod = String(row?.method ?? "")
                        .trim()
                        .toLowerCase();

                    const isFacilityGeneral =
                        /facility\s*\/\s*general/i.test(previousMethod);

                    const sameOptInStatus =
                        externalFormOptIn !== null &&
                        patientCurrentOptInFlag !== null &&
                        externalFormOptIn === patientCurrentOptInFlag;

// Allow duplicate opt-in only when the previous
// active consent method contains "facility/general".
                    const allowFacilityGeneralDoubleOptIn =
                        externalFormOptIn === true &&
                        patientCurrentOptInFlag === true &&
                        isFacilityGeneral;

                    const shouldBlock =
                        sameOptInStatus &&
                        !allowFacilityGeneralDoubleOptIn;

                    if (shouldBlock) {
                        nextDoNotLinkMap[patientId] = row;
                    }
                });

                setDoNotLinkMap(nextDoNotLinkMap);
            } catch (err) {
                if (err?.name === "AbortError") return;
                setPatients([]);
                setDoNotLinkMap({});
                setPatientsError(err?.message || "Failed to fetch patients.");
            } finally {
                setPatientsLoading(false);
            }
        },
        [kc?.token, externalFormOptIn]
    );

    const handleSelectFacility = (row) => {
        setSelectedFacility(row);
        setShowFacilityOptions(false);

        setPatients([]);
        setPatientsError("");
        setPatientsLoading(false);
        setSelectedPatient(null);

        // Seed patient search from the external form first name
        setPatientSearch(getSeedPatientSearchName(externalFormRow));

        setPatientsFetchedForFacilityKey("");
        setDoNotLinkMap({});
        setLatestConsentByPatient({});

        setShowPatientOptions(true);
    };

    const unselectFacility = () => {
        setSelectedFacility(null);
        setShowFacilityOptions(true);

        setSelectedPatient(null);
        setPatientSearch("");
        setPatients([]);
        setPatientsError("");
        setPatientsLoading(false);
        setPatientsFetchedForFacilityKey("");
        setDoNotLinkMap({});
        setShowPatientOptions(true);
    };

    const unselectPatient = () => {
        setSelectedPatient(null);
        setShowPatientOptions(true);
    };

    // When patientSearch changes: only do work if facility selected and length >= 3
    React.useEffect(() => {
        if (!open) return;
        if (!selectedFacility) return;
        if (!showPatientOptions) return;

        const q = String(patientSearch || "").trim();

        if (q.length < 3) {
            setSelectedPatient(null);
            setPatientsError("");
            return;
        }

        if (patientDebounceRef.current) clearTimeout(patientDebounceRef.current);

        patientDebounceRef.current = setTimeout(() => {
            const facilityKey = [
                selectedFacility?.fac_id ?? "",
                selectedFacility?.ouid ?? "",
                selectedFacility?.ehr_ds ?? "",
            ].join("|||");

            if (patientsFetchedForFacilityKey !== facilityKey) {
                setPatientsFetchedForFacilityKey(facilityKey);
                fetchPatients({
                    fac_id: selectedFacility?.fac_id,
                    ouid: selectedFacility?.ouid,
                    ehr_ds: selectedFacility?.ehr_ds,
                });
            }
        }, 300);

        return () => {
            if (patientDebounceRef.current) clearTimeout(patientDebounceRef.current);
        };
    }, [
        patientSearch,
        selectedFacility,
        open,
        fetchPatients,
        patientsFetchedForFacilityKey,
        showPatientOptions,
    ]);

    const filteredPatients = React.useMemo(() => {
        const q = String(patientSearch || "").trim().toLowerCase();
        if (q.length < 3) return [];
        return patients.filter((p) => {
            const first = String(p?.first_name ?? "").toLowerCase();
            const last = String(p?.last_name ?? "").toLowerCase();
            const mrn = String(p?.mrn_id ?? "").toLowerCase();
            const dob = String(p?.dob ?? "").slice(0, 10).toLowerCase();
            const status = String(p?.status ?? "").toLowerCase();
            return (
                first.includes(q) ||
                last.includes(q) ||
                mrn.includes(q) ||
                dob.includes(q) ||
                status.includes(q)
            );
        });
    }, [patients, patientSearch]);

    const canShowPatientResults =
        Boolean(selectedFacility) && String(patientSearch || "").trim().length >= 3;

    const blockedPatientsInView = React.useMemo(() => {
        return filteredPatients.filter((p) => {
            const patientId = String(p?.patient_id ?? p?.patient_unique_id ?? "").trim();
            return Boolean(doNotLinkMap[patientId]);
        });
    }, [filteredPatients, doNotLinkMap]);



    const submitLink = async () => {
        if (!externalFormRow?.id) {
            setLinkError("Missing external form id.");
            return;
        }
        if (!selectedFacility || !selectedPatient) return;
        if (!effectiveDate) {
            setLinkError("Effective date is required.");
            return;
        }

        const selectedPatientId = String(
            selectedPatient?.patient_id ?? selectedPatient?.patient_unique_id ?? ""
        ).trim();

        if (selectedPatientId && doNotLinkMap[selectedPatientId]) {
            setLinkError(
                "This patient cannot be linked because their current " +
                "opt-in status already matches the external form. " +
                "Duplicate opt-in is only permitted for Facility/General consent."
            );
            return;
        }

        setLinkSubmitting(true);
        setLinkError("");
        setLinkSuccess("");

        try {
            const linkPayload = {
                external_form_row: externalFormRow,
                external_form_id: externalFormRow.id,
                selected_facility: selectedFacility,
                selected_patient: selectedPatient,
                effective_date: effectiveDate,
                createdBy: { givenName, familyName, email },
            };

            console.log(linkPayload);


            const linkResp = await fetch(
                `${process.env.REACT_APP_API_URL}/consent/link_external_form_to_patient_consent`,
                {
                    method: "POST",
                    headers: {
                        Authorization: kc?.token ? `Bearer ${kc.token}` : undefined,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(linkPayload),
                }
            );

            if (!linkResp.ok) {
                const text = await linkResp.text().catch(() => "");
                throw new Error(text || `Link failed (HTTP ${linkResp.status})`);
            }

            const linkJson = await linkResp.json();

            const statusResp = await fetch(
                `${process.env.REACT_APP_API_URL}/consent/change-form-status`,
                {
                    method: "POST",
                    headers: {
                        Authorization: kc?.token ? `Bearer ${kc.token}` : undefined,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        id: externalFormRow.id,
                        status: "completed",
                    }),
                }
            );

            if (!statusResp.ok) {
                const text = await statusResp.text().catch(() => "");
                throw new Error(text || `Status update failed (HTTP ${statusResp.status})`);
            }

            const statusJson = await statusResp.json();

            setLinkSuccess("Linked successfully and marked as completed.");

            if (typeof onLinked === "function") {
                onLinked({
                    id: externalFormRow.id,
                    linkResponse: linkJson,
                    statusResponse: statusJson,
                });
            }

            onClose?.();
        } catch (e) {
            setLinkError(e?.message || "Linking failed.");
        } finally {
            setLinkSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="h6">Link To Patient</Typography>
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                    External form: <strong>{displayPatientName || "—"}</strong>
                    {displayPatientDob ? (
                        <>
                            , dob <strong>{String(displayPatientDob).slice(0, 10)}</strong>
                        </>
                    ) : null}{" "}
                    at <strong>{displayFacilityName || "—"}</strong>{" "}
                    {displayProviderNumber ? `(${displayProviderNumber})` : ""}.
                </Alert>

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    1) Choose Facility
                </Typography>

                <TextField
                    label="Facility Search (defaults to provider #)"
                    value={orgSearchTerm}
                    onChange={(e) => setOrgSearchTerm(e.target.value)}
                    fullWidth
                    helperText={
                        selectedFacility
                            ? "Facility selected. Click “Change facility” to search again."
                            : "Type at least 2 characters. Search by facility name or provider number."
                    }
                    disabled={Boolean(selectedFacility)}
                />

                {selectedFacility && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button size="small" variant="text" onClick={unselectFacility}>
                            Change facility
                        </Button>
                    </Stack>
                )}

                {showFacilityOptions && orgError && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {orgError}
                    </Alert>
                )}

                {showFacilityOptions && orgLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {showFacilityOptions && !orgLoading && organizationOptions.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 1.5, overflow: "hidden" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Facility</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Provider #</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>fac_id</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>ouid</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>City/State</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizationOptions.map((r, i) => (
                                    <TableRow
                                        key={`${r?.facility_unique_id ?? "x"}-${r?.ouid ?? "y"}-${i}`}
                                        sx={rowButtonSx}
                                        onClick={() => handleSelectFacility(r)}
                                        title="Click to select this facility"
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {r?.facility_name || "—"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                facility_unique_id: {r?.facility_unique_id ?? "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{r?.source_id ?? "—"}</TableCell>
                                        <TableCell>{r?.fac_id ?? "—"}</TableCell>
                                        <TableCell
                                            sx={{
                                                maxWidth: 260,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {r?.ouid ?? "—"}
                                        </TableCell>
                                        <TableCell>
                                            {(r?.city || "—") + ", " + (r?.state || "—")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                {!showFacilityOptions && selectedFacility && (
                    <Alert severity="success" sx={{ mt: 1.5 }}>
                        Selected facility: <strong>{selectedFacility?.facility_name}</strong>{" "}
                        (provider: {selectedFacility?.source_id ?? "—"}, fac_id:{" "}
                        {selectedFacility?.fac_id ?? "—"})
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    2) Choose Patient
                </Typography>

                <TextField
                    label="Patient Search"
                    value={patientSearch}
                    onChange={(e) => {
                        setPatientSearch(e.target.value);
                        if (selectedFacility) setShowPatientOptions(true);
                    }}
                    fullWidth
                    disabled={!selectedFacility || Boolean(selectedPatient)}
                    helperText={
                        !selectedFacility
                            ? "Select a facility first."
                            : selectedPatient
                                ? "Patient selected. Click “Change patient” to search again."
                                : "Type at least 3 characters to search patients."
                    }
                />

                {selectedPatient && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button size="small" variant="text" onClick={unselectPatient}>
                            Change patient
                        </Button>
                    </Stack>
                )}

                {showPatientOptions && patientsError && canShowPatientResults && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {patientsError}
                    </Alert>
                )}

                {showPatientOptions && patientsLoading && canShowPatientResults && (
                    <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {showPatientOptions &&
                    canShowPatientResults &&
                    !patientsLoading &&
                    blockedPatientsInView.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            {blockedPatientsInView.length} patient
                            {blockedPatientsInView.length === 1 ? "" : "s"} cannot be linked.
                            Duplicate opt-in is only allowed when the previous
                            consent method contains Facility/General.
                            Duplicate opt-out is not allowed.
                        </Alert>
                    )}

                {showPatientOptions &&
                    canShowPatientResults &&
                    !patientsLoading &&
                    filteredPatients.length === 0 &&
                    !patientsError && (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            No matching patients found.
                        </Alert>
                    )}

                {showPatientOptions &&
                    canShowPatientResults &&
                    !patientsLoading &&
                    filteredPatients.length > 0 && (
                        <Paper variant="outlined" sx={{ mt: 1.5, overflow: "hidden" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>First</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Last</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>MRN</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>DOB</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Consent Method</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Link Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPatients.map((p, i) => {
                                        const key = p?.patient_id ?? p?.patient_unique_id ?? i;
                                        const patientId = String(
                                            p?.patient_id ?? p?.patient_unique_id ?? ""
                                        ).trim();
                                        const blockedConsentRow = doNotLinkMap[patientId];
                                        const isBlocked = Boolean(blockedConsentRow);
                                        const blockedOptInFlag = normalizeBoolean(blockedConsentRow?.opt_in_flag);

                                        const latestConsent = latestConsentByPatient[patientId];

                                        const consentMethod =
                                            latestConsent?.method?.trim() || "—";

                                        return (
                                            <TableRow
                                                key={key}
                                                sx={isBlocked ? disabledRowSx : rowButtonSx}
                                                onClick={() => {
                                                    if (isBlocked) return;
                                                    setSelectedPatient(p);
                                                    setShowPatientOptions(false);
                                                    setLinkError("");
                                                }}
                                                title={
                                                    isBlocked
                                                        ? "This patient cannot be linked"
                                                        : "Click to select this patient"
                                                }
                                            >
                                                <TableCell>{p?.first_name ?? "—"}</TableCell>
                                                <TableCell>{p?.last_name ?? "—"}</TableCell>
                                                <TableCell>{p?.mrn_id ?? "—"}</TableCell>
                                                <TableCell>{formatDob(p?.dob)}</TableCell>
                                                <TableCell>{p?.status ?? "—"}</TableCell>

                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {consentMethod}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    {isBlocked ? (
                                                        <Typography variant="body2" color="error">
                                                            Do not link
                                                            {blockedOptInFlag === true
                                                                ? " (current: opt in)"
                                                                : blockedOptInFlag === false
                                                                    ? " (current: opt out)"
                                                                    : ""}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="success.main">
                                                            Can link
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}

                {!showPatientOptions && selectedPatient && (
                    <Alert severity="success" sx={{ mt: 1.5 }}>
                        Selected patient:{" "}
                        <strong>
                            {selectedPatient?.first_name ?? ""} {selectedPatient?.last_name ?? ""}
                        </strong>{" "}
                        (MRN: {selectedPatient?.mrn_id ?? "—"}, DOB: {formatDob(selectedPatient?.dob)})
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    3) Effective Date
                </Typography>

                <TextField
                    label="Effective Date"
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="Defaults to the date the external form was filled out."
                />

                {linkError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {linkError}
                    </Alert>
                )}

                {linkSuccess && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {linkSuccess}
                    </Alert>
                )}

                <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: "flex-end" }}>
                    <Button variant="text" onClick={onClose} disabled={linkSubmitting}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={
                            !selectedFacility ||
                            !selectedPatient ||
                            !effectiveDate ||
                            linkSubmitting ||
                            Boolean(
                                doNotLinkMap[
                                    String(
                                        selectedPatient?.patient_id ??
                                        selectedPatient?.patient_unique_id ??
                                        ""
                                    ).trim()
                                    ]
                            )
                        }
                        onClick={submitLink}
                    >
                        {linkSubmitting ? "Linking..." : "Link"}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
}